'use client'

import React, { Suspense } from 'react'
import { Provider } from 'react-redux'
import { useDispatch } from 'react-redux'
import type { AppDispatch } from '@/store'
import { fetchUser } from '@/store'
import { store } from '@/store'
import { LanguageProvider } from '@/contexts/LanguageContext'
import type { Language, Translations } from '@/lib/i18n/types'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { AuthProvider } from '@/contexts/AuthContext'
import { RoleProvider } from '@/contexts/RoleContext'
import { PaymentReturnHandler } from '@/components/PaymentReturnHandler'
import { SentryScopeSync } from '@/components/SentryScopeSync'
import { CookieConsentBanner } from '@/components/CookieConsentBanner'
import { useCaptureGTMAttribution, useTrackDeclarativeAnalytics } from '@/lib/gtm'
import { IS_CN_SITE } from '@/config/site'

interface ProvidersProps {
  children: React.ReactNode;
  enableAnalytics: boolean;
  versionUpdateNotifier?: React.ReactNode;
  initialLocale: Language;
  initialMessages: Translations;
}

export function Providers({ children, enableAnalytics, versionUpdateNotifier, initialLocale, initialMessages }: ProvidersProps) {
  // Internal component to hydrate user on mount after Provider is in place
  const HydrateUser = () => {
    const dispatch = useDispatch<AppDispatch>()
    React.useEffect(() => {
      console.log('[HydrateUser] dispatching fetchUser')
      void dispatch(fetchUser())
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    return null
  }
  const AnalyticsSync = () => {
    useCaptureGTMAttribution()
    useTrackDeclarativeAnalytics(IS_CN_SITE)
    return null
  }

  return (
    <Provider store={store}>
      <HydrateUser />
      <Suspense fallback={null}>
        <AnalyticsSync />
      </Suspense>
      <SentryScopeSync />
      <ThemeProvider>
        <LanguageProvider
          defaultLanguage={IS_CN_SITE ? 'zh' : 'en'}
          enforceDefaultLanguage={IS_CN_SITE}
          initialLocale={initialLocale}
          initialMessages={initialMessages}
        >
          <AuthProvider>
            <RoleProvider>
              <Suspense fallback={null}>
                <PaymentReturnHandler />
              </Suspense>
              {versionUpdateNotifier}
              {children}
              <CookieConsentBanner enabled={enableAnalytics} />
            </RoleProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </Provider>
  )
}
